import { useEffect, useState } from 'react';
import { mensajeDeError, useSinConexion } from '@/core/red/conectividad';
import { NecesitaConexion } from '@/ui/NecesitaConexion';
import { Chip } from '@/ui/Chips';
import { useOrg } from '@/features/team/OrgProvider';
import { usePeriod } from '@/features/period/PeriodProvider';
import { formatCents, MESES } from '@/features/catalog/catalog';
import { fetchDashboard, type DashboardSummary } from '@/features/dashboard/dashboard';
import { fetchRepartoDelMes, type RepartoDelMes } from '@/features/dashboard/reparto';
import { FILTROS_VACIOS, listWorks, type WorkListRow } from '@/features/works/works';
import { exportarExcel } from './exportFiles';
import { fetchReportByClient, type ClientReportRow } from './reports';

interface Props {
  onVolver: () => void;
}

type Vista = 'mes' | 'clientes' | 'participantes';

/**
 * Reportes del período (Spec §35–§37).
 *
 * Tres miradas del mismo mes: el detalle de trabajos con su estado
 * administrativo, el resumen por cliente y el resultado por participante. La
 * última la devuelve la base sólo a un administrador, así que acá no hay nada
 * que ocultar: si no llegó, no existe.
 */
export function ReportsPage({ onVolver }: Props) {
  const { activeOrg } = useOrg();
  // Mismo período que el dashboard, sin volver a decidirlo.
  const { year, month } = usePeriod();
  const orgId = activeOrg?.organization_id;
  const esAdmin = activeOrg?.is_admin ?? false;

  const [vista, setVista] = useState<Vista>('mes');
  const [works, setWorks] = useState<WorkListRow[]>([]);
  const [porCliente, setPorCliente] = useState<ClientReportRow[]>([]);
  const [resumen, setResumen] = useState<DashboardSummary | null>(null);
  const [reparto, setReparto] = useState<RepartoDelMes | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    let cancelado = false;
    void (async () => {
      setCargando(true);
      try {
        const [w, c, d, r] = await Promise.all([
          listWorks(orgId, { ...FILTROS_VACIOS, year, month }, 1000),
          fetchReportByClient(orgId, year, month),
          fetchDashboard(orgId, year, month),
          // El reparto es del administrador y la base lo rechaza para el resto.
          // No se pide si no corresponde: pedirlo para descartar el error sería
          // pedirle al servidor que diga que no.
          esAdmin ? fetchRepartoDelMes(orgId, year, month) : Promise.resolve(null),
        ]);
        if (cancelado) return;
        setWorks(w);
        setPorCliente(c);
        setResumen(d);
        setReparto(r);
        setError(null);
      } catch (e) {
        if (!cancelado) setError(mensajeDeError(e));
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [orgId, year, month, esAdmin]);

  const exportar = async () => {
    if (!activeOrg) return;
    setError(null);
    setExportando(true);
    try {
      await exportarExcel({
        organizationName: activeOrg.name,
        year,
        month,
        works,
        byClient: porCliente,
        // Si el usuario no es administrador esto llega null y la hoja no se crea.
        participants: resumen?.org_result ?? null,
        reparto,
      });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setExportando(false);
    }
  };

  // Sin servidor no hay nada honesto que mostrar acá: haría falta el estado
  // de ahora, y de ahora sólo sabe el servidor.
  if (useSinConexion()) {
    return (
      <div className="flex flex-col gap-5 p-4">
        <NecesitaConexion
          que="Los reportes se arman con los datos del servidor, no con la copia del teléfono."
          testId="reportes-sin-conexion"
        />
      </div>
    );
  }

  if (!activeOrg || !orgId) return null;

  // D5: el placeholder cuenta en la plata, pero NO en el ranking comercial de
  // clientes. Se muestra aparte, con su etiqueta, para que nadie lo confunda
  // con un cliente y para que el total del período siga cerrando.
  const comerciales = porCliente.filter((c) => c.record_kind !== 'legacy_placeholder');
  const legacy = porCliente.filter((c) => c.record_kind === 'legacy_placeholder');

  const pendientes = works.filter((w) => w.price_pending).length;
  const totalDeterminado = works.reduce((acc, w) => acc + (w.total_cents ?? 0), 0);

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="reports-page">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          Reportes · {MESES[month - 1]} {year}
        </h1>
        <button onClick={onVolver} className="min-h-11 text-sm underline opacity-70">
          Volver
        </button>
      </header>

      {error && (
        <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="reports-error">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => void exportar()}
        disabled={exportando || cargando}
        className="min-h-12 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 font-medium text-[var(--sobre-accion)] disabled:opacity-50"
        data-testid="export-excel"
      >
        {exportando ? 'Preparando la planilla…' : 'Descargar Excel del período'}
      </button>

      {/* La primera exportación de la sesión baja ~930 kB de la biblioteca que
          arma la planilla. No afecta el arranque —se carga en diferido— pero
          con señal de campo tarda, y sin avisar parece que el botón no anduvo. */}
      <p className="-mt-2 text-xs text-[var(--tinta-tenue)]" data-testid="export-aviso">
        La primera vez descarga la herramienta que arma la planilla. Con poca señal puede demorar;
        después queda lista.
      </p>

      <div className="flex gap-2 overflow-x-auto" data-testid="report-tabs">
        {(
          [
            ['mes', 'Del mes'],
            ['clientes', 'Por cliente'],
            ['participantes', 'Por participante'],
          ] as [Vista, string][]
        ).map(([id, label]) => (
          <Chip
            key={id}
            activo={vista === id}
            onClick={() => setVista(id)}
            testId={`report-tab-${id}`}
          >
            {label}
          </Chip>
        ))}
      </div>

      {cargando && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

      {vista === 'mes' && (
        <section className="superficie p-4" data-testid="report-month">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 className="font-medium">
              {works.length} trabajo{works.length === 1 ? '' : 's'}
            </h2>
            <span className="text-sm text-[var(--tinta-suave)]">
              {formatCents(totalDeterminado)}
              {pendientes > 0 && (
                <span className="ml-1 text-[var(--aviso)]">+ {pendientes} sin precio</span>
              )}
            </span>
          </div>
          <ul
            className="flex flex-col divide-y divide-[var(--linea)]"
            data-testid="report-month-list"
          >
            {works.map((w) => (
              <li key={w.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{w.client_name}</span>
                  <span className="block truncate text-xs text-[var(--tinta-suave)]">
                    {w.work_date} · {w.service_names}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block">{formatCents(w.total_cents)}</span>
                  <span className="block text-xs text-[var(--tinta-suave)]">
                    {w.is_paid ? 'cobrado' : w.is_billed ? 'facturado' : 'sin facturar'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {vista === 'clientes' && (
        <section className="superficie p-4" data-testid="report-clients">
          <h2 className="mb-3 font-medium">Por cliente y establecimiento</h2>
          {porCliente.length === 0 ? (
            <p className="text-sm text-[var(--tinta-suave)]">Sin trabajos en este período.</p>
          ) : (
            <ul
              className="flex flex-col divide-y divide-[var(--linea)]"
              data-testid="report-clients-list"
            >
              {comerciales.map((c) => (
                <li
                  key={`${c.client_id}-${c.establishment_id ?? 'sin'}`}
                  className="flex flex-col gap-1 py-2 text-sm"
                >
                  <div className="flex justify-between gap-3">
                    <span className="min-w-0 truncate font-medium">
                      {c.client_name}
                      {c.establishment_name ? ` · ${c.establishment_name}` : ''}
                    </span>
                    <span className="shrink-0">{formatCents(c.total_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[var(--tinta-suave)]">
                    <span>
                      {c.works_count} trabajo{c.works_count === 1 ? '' : 's'} · {c.total_kg} kg
                    </span>
                    <span>
                      {c.pending_to_collect_count > 0
                        ? `a cobrar ${formatCents(c.pending_to_collect_cents)}`
                        : 'todo cobrado'}
                    </span>
                  </div>
                  {c.pending_count > 0 && (
                    <span className="text-xs text-[var(--aviso)]">
                      {c.pending_count} sin precio de referencia
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {legacy.length > 0 && (
            <div
              className="mt-4 border-t border-[var(--linea)] pt-3"
              data-testid="report-legacy-block"
            >
              <h3 className="text-xs font-medium uppercase opacity-60">Registros legacy</h3>
              <p className="mb-2 text-xs text-[var(--tinta-suave)]">
                Trabajos históricos sin cliente identificable. Cuentan en los totales del período y
                en la liquidación; no son clientes y quedan fuera del ranking comercial.
              </p>
              <ul className="flex flex-col divide-y divide-[var(--linea)]">
                {legacy.map((c) => (
                  <li
                    key={`${c.client_id}-${c.establishment_id ?? 'sin'}`}
                    className="flex justify-between gap-3 py-2 text-sm"
                  >
                    <span className="min-w-0 truncate">{c.client_name}</span>
                    <span className="shrink-0">
                      {c.works_count} · {formatCents(c.total_cents)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {vista === 'participantes' && (
        <section className="superficie p-4" data-testid="report-participants">
          <h2 className="mb-3 font-medium">Por participante</h2>
          {resumen?.org_result ? (
            <ul
              className="flex flex-col divide-y divide-[var(--linea)]"
              data-testid="report-participants-list"
            >
              {resumen.org_result.map((p) => (
                <li key={p.participant_id} className="flex flex-col gap-1 py-2 text-sm">
                  <div className="flex justify-between font-medium">
                    <span>{p.display_name}</span>
                    <span
                      className={
                        p.balance_cents < 0 ? 'text-[var(--peligro)]' : 'text-[var(--exito)]'
                      }
                    >
                      {formatCents(p.balance_cents)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-[var(--tinta-suave)]">
                    <span>Le corresponde</span>
                    <span>{formatCents(p.earned_honoraria_cents + p.earned_mobility_cents)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[var(--tinta-suave)]">
                    <span title="Total facturado a nombre de esta persona en el período, cobrado o no.">
                      Facturación asignada
                    </span>
                    <span>{formatCents(p.billed_cents)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--tinta-suave)]" data-testid="participants-unavailable">
              {resumen?.is_admin === false
                ? 'El resultado por participante es exclusivo del administrador. Tu propio resultado está en Inicio.'
                : resumen && resumen.production.pending_count > 0
                  ? 'Se calcula cuando todos los trabajos del mes tengan precio de referencia.'
                  : 'Sin datos para este período.'}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
